// src/padplanner/NewPlanPage.jsx
//
// /padplanner/new, mockup 3: a panel with the plan's details and its
// perimeter, beside a map to draw, pick or check that perimeter on, with the
// imagery under it on a time strip.
//
// ── ONE CLICK, UP TO THREE REQUESTS ────────────────────────────────────────
//
//   1  POST /plans: title, type, target, owner, project, wells, and a drawn or
//      uploaded perimeter with its perimeter_source. One transaction: if it
//      fails, nothing was saved and the form stays filled.
//   2  a picked pad: PATCH revisions/current {pad_observation_id}. The server
//      copies the pad's geometry; the client never asserts a pad's shape.
//   3  a chosen capture: PATCH revisions/current {imagery}. The server
//      resolves it against the saved perimeter and may refuse it.
//
// If 2 or 3 fails, the plan exists. The plan page opens and says exactly what
// was saved and what was not (planNotices.js); a later step is not tried
// without the perimeter it needs.
//
// ── OWNER AND PROJECT ──────────────────────────────────────────────────────
//
// Owner is Me (the server's default, so the field is not sent) or No owner
// (null): there is no member list to pick another person from (decision 2).
// Project is any project the picker offers, or none. It starts at the
// picker's project, visibly, so a plan made while a project is selected does
// not vanish from the list it was made from. The server checks it is an
// active project of this org (project_invalid).
//
// ── REQUIRES LANDOWNER APPROVAL (158) ─────────────────────────────────────
//
// Off by default. On, it names the landowner from the org's live
// relationships (IntendedLandownerField.jsx), sent as intended_landowner_org_id
// with request 1; the server checks the relationship again (409
// no_landowner_relationship, nothing saved). Turned on with none chosen,
// Create plan asks for one rather than sending a plan without it.
//
// ── CUT ────────────────────────────────────────────────────────────────────
//
//   S3  "Start from" (layout templates) and the wellhead dots
//   S8  Import Visio
//   --  "+ Upload ortho" (deferred), edge-length labels on the ring (MlDraw
//       draws none), the overview minimap, the owner text field (decision 2);
//       "Create & open editor" is "Create plan" (the editor is S4)
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Check, X } from "lucide-react";
import PadPlannerGate from "./PadPlannerGate";
import PadPlannerMap from "./PadPlannerMap";
import SidePanel, { SectionLabel } from "./SidePanel";
import { ImageryStrip } from "../components/imagery/TimeStrip";
import { selectAndZoom, sitePlannerRows } from "./imageryStripRows";
import {
  createPlan, updateRevision, lookupWells, getPad, getEpochsAt, parsePerimeterFile, getLandownerRelationships,
} from "../api/padplannerApi";
import IntendedLandownerField from "./IntendedLandownerField";
import { INTENDED_CHOOSE_TEXT } from "./landowner";
import { useProjectStore } from "../stores/useProjectStore";
import { usePlanProjects, PROJECTS_FAILED_TEXT } from "./usePlanProjects";
import { PLAN_TYPES, PLAN_TYPE_LABELS, formatAcres, formatApi10, imageryEmptyText, imageryLabel, chipDate, includeForCapture, referenceForCapture, creditForCapture } from "./planVocabulary";
import { refusalText } from "./planRefusals";
import { areaM2, formatPerimeterFt, listingPoint } from "./planMapData";
import { createNotice, leaveNotice } from "./planNotices";
import { PADPLANNER_DISPLAY_NAME } from "../constants/analyticsProducts";

export default function NewPlanPage() {
  return (
    <PadPlannerGate section="plans">
      <NewPlanForm />
    </PadPlannerGate>
  );
}

// The base imagery is chosen on the strip: a click on a collect's date marks
// it BASE, draws it on the map, and pins it when the plan is created
// (request 3). Nothing is chosen until a date is clicked; a Sentinel-2 date
// is drawn when clicked but is context only, never chosen. Clicking the
// chosen date again, or Clear beside the line that names it, un-chooses it
// (design §16, S2 open item 3): before, only a new perimeter did.
export const BASE_PICKED_TEXT = "Base imagery is picked on the timeline under the map.";
export const BASE_NOT_CHOSEN_TEXT = "No base imagery chosen. Click a date on the imagery strip to save it with the plan.";
export const BASE_SAVED_TEXT = "Saved with the plan when you create it.";

const SOURCE_WORDS = { drawn: "Perimeter drawn", file: "Perimeter uploaded", pad: "Perimeter from a detected pad" };
const field = "rounded border border-bg-border bg-bg px-2 py-1.5 text-[13px] text-fg-primary";

function Segmented({ label, options, value, onChange }) {
  return (
    <div className="flex flex-col gap-1" role="group" aria-label={label}>
      <span className="op-label uppercase">{label}</span>
      <div className="flex flex-wrap gap-1">
        {options.map(([v, text]) => (
          <button key={v} type="button" aria-pressed={value === v} onClick={() => onChange(v)}
            className={`rounded border px-2.5 py-1 text-[12.5px] ${value === v ? "border-accent-action bg-accent-action/15 text-fg-primary" : "border-bg-border text-fg-secondary hover:text-fg-primary"}`}>
            {text}
          </button>
        ))}
      </div>
    </div>
  );
}

function NewPlanForm() {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const planProjects = usePlanProjects();
  const activeProjectId = useProjectStore((s) => s.activeProjectId);

  const [title, setTitle] = useState("");
  const [planType, setPlanType] = useState("drilling");
  const [targetDate, setTargetDate] = useState("");
  const [owner, setOwner] = useState("me");
  // null until the user picks: then the picker's project, once the list has
  // loaded and only if it is one a plan may carry.
  const [chosenProjectId, setProjectId] = useState(null);
  const projects = planProjects.projects ?? [];
  const projectId = chosenProjectId ?? (projects.some((p) => p.id === activeProjectId) ? activeProjectId : "");
  const [wells, setWells] = useState([]);       // [{api10, source, label, note}]
  // null (off) | "" (on, none chosen) | an org id
  const [landownerId, setLandownerId] = useState(null);
  const [rels, setRels] = useState({ relationships: null, failed: false });
  useEffect(() => {
    let cancelled = false;
    getLandownerRelationships()
      .then((r) => { if (!cancelled) setRels({ relationships: r, failed: false }); })
      .catch(() => { if (!cancelled) setRels({ relationships: null, failed: true }); });
    return () => { cancelled = true; };
  }, []);

  // {geometry, source: 'drawn'|'file'|'pad', label, observationId?, area_m2?}
  const [pending, setPending] = useState(null);
  const [picking, setPicking] = useState(false);
  const [lanes, setLanes] = useState(null);
  const [cursorKey, setCursorKey] = useState(null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [perimeterError, setPerimeterError] = useState(null);

  // The imagery over the outline, listed at one point of it (advisory).
  const geometry = pending?.geometry ?? null;
  useEffect(() => {
    const at = geometry ? listingPoint(geometry) : null;
    if (!at) return undefined;
    let cancelled = false;
    getEpochsAt(at)
      .then((r) => { if (!cancelled) setLanes(r); })
      .catch(() => { if (!cancelled) setLanes(null); });
    return () => { cancelled = true; };
  }, [geometry]);
  const shownLanes = geometry ? lanes : null;
  const chosenKey = shownLanes?.collects?.some((c) => c.dataset_key === cursorKey) ? cursorKey : null;
  const chosen = chosenKey ? shownLanes.collects.find((c) => c.dataset_key === chosenKey) : null;

  // A second click un-chooses; a zoom-in chip's click only chooses (S-e5: it is a click to see the capture).
  const onCursor = (key, chip) => setCursorKey((cur) => (cur === key && !Number.isInteger(chip?.zoomIn) ? null : key));

  function replacePending(next) {
    setPending(next);
    setCursorKey(null);
    setPerimeterError(null);
  }

  function onDraw(g) {
    replacePending(g ? { geometry: g, source: "drawn", label: "drawn on the map" } : null);
  }
  function startDraw() {
    setPicking(false);
    replacePending(null);
    mapRef.current?.startDraw();
  }
  function togglePick() {
    mapRef.current?.clearDraw();
    setPicking((v) => !v);
  }
  async function onPadPick(observationId) {
    setPicking(false);
    setPerimeterError(null);
    try {
      const pad = await getPad(observationId);
      if (!pad.usable_as_perimeter) { setPerimeterError(refusalText(pad.refusal)); return; }
      replacePending({ geometry: pad.geometry, source: "pad", label: "a detected pad", observationId, area_m2: pad.area_m2 });
    } catch (err) { setPerimeterError(refusalText(err)); }
  }
  async function onFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    mapRef.current?.clearDraw();
    setPicking(false);
    setPerimeterError(null);
    try {
      const r = await parsePerimeterFile(file);
      replacePending({ geometry: r.geometry, source: "file", label: file.name, area_m2: r.area_m2 });
    } catch (err) { setPerimeterError(refusalText(err)); }
  }
  function clearPerimeter() {
    mapRef.current?.clearDraw();
    setPicking(false);
    replacePending(null);
  }

  async function create() {
    if (!title.trim()) { setError("Give the plan a title."); return; }
    if (landownerId === "") { setError(INTENDED_CHOOSE_TEXT); return; }
    setBusy(true);
    setError(null);
    const body = { title: title.trim(), plan_type: planType };
    if (targetDate) body.target_date = targetDate;
    if (owner === "none") body.owner_user_id = null;
    if (projectId) body.project_id = projectId;
    if (landownerId) body.intended_landowner_org_id = landownerId;
    if (wells.length) body.wells = wells.map((w) => ({ api: w.api10, source: w.source, label: w.label ?? null }));
    // perimeter_source is our word for the audit row; the server records it
    // as client-declared and decides nothing by it.
    if (pending && pending.source !== "pad") {
      body.perimeter = pending.geometry;
      body.perimeter_source = pending.source === "file" ? "uploaded" : "drawn";
    }

    let plan;
    try {
      ({ plan } = await createPlan(body));
    } catch (err) {
      // Nothing was saved: the form keeps everything for another try.
      // 403 is a read-only seat that reached this page by typing the path.
      setError(err.status === 403 ? "Your license can view plans but not create them." : refusalText(err));
      setBusy(false);
      return;
    }

    const saved = { perimeter: Boolean(body.perimeter), wells: wells.length };
    let failure = null;
    if (pending?.source === "pad") {
      try {
        await updateRevision(plan.id, { pad_observation_id: pending.observationId });
        saved.perimeter = true;
      } catch (err) { failure = { step: "pad", reason: refusalText(err) }; }
    }
    if (!failure && chosenKey && saved.perimeter) {
      try {
        await updateRevision(plan.id, { imagery: { dataset_key: chosenKey } });
      } catch (err) { failure = { step: "imagery", reason: refusalText(err) }; }
    }
    const notice = createNotice(plan.plan_number, saved, failure, Boolean(chosenKey));
    if (notice) leaveNotice(plan.id, notice);
    navigate({ to: "/padplanner/plans/$planId", params: { planId: plan.id } });
  }

  const area = pending ? (pending.area_m2 ?? areaM2(pending.geometry)) : null;
  const head = (
    <>
      <nav className="op-meta flex items-center gap-1" aria-label="Breadcrumb">
        <button type="button" className="hover:text-fg-primary" onClick={() => navigate({ to: "/padplanner" })}>{PADPLANNER_DISPLAY_NAME}</button>
        <span aria-hidden="true">/</span>
        <span className="text-fg-primary">New</span>
      </nav>
      <h1 className="font-display text-[18px] font-semibold text-fg-primary">New plan</h1>
    </>
  );
  const footer = (
    <>
      <button type="button" className="op-btn op-btn--sm" onClick={() => navigate({ to: "/padplanner" })}>Cancel</button>
      <button type="button" className="op-btn op-btn--primary op-btn--sm" disabled={busy} onClick={create}>Create plan</button>
    </>
  );

  return (
    // h-full: the shell's <main> is a block box, where flex-1 does nothing.
    // The panel scrolls inside; its footer (Cancel / Create plan) stays put.
    <div className="flex h-full min-h-0">
      <SidePanel label="New plan" head={head} footer={footer}>
        <section className="flex flex-col gap-3" aria-labelledby="pp-new-details">
          <SectionLabel id="pp-new-details">1 · Details</SectionLabel>
          <label className="flex flex-col gap-1">
            <span className="op-label uppercase">Name</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} autoFocus className={field} />
          </label>
          <Segmented label="Type" value={planType} onChange={setPlanType} options={PLAN_TYPES.map((t) => [t, PLAN_TYPE_LABELS[t]])} />
          <label className="flex flex-col gap-1">
            <span className="op-label uppercase">Target start</span>
            <input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} className={field} />
          </label>
          <Segmented label="Owner" value={owner} onChange={setOwner} options={[["me", "Me"], ["none", "No owner"]]} />
          <label className="flex flex-col gap-1">
            <span className="op-label uppercase">Project</span>
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className={field}>
              <option value="">No project</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
          {planProjects.failed && <p className="text-xs text-fg-secondary">{PROJECTS_FAILED_TEXT}</p>}
          <IntendedLandownerField relationships={rels.relationships} failed={rels.failed} value={landownerId} onChange={setLandownerId} />
          <WellsField wells={wells} onChange={setWells} />
        </section>

        <section className="flex flex-col gap-3 border-t border-bg-border pt-4" aria-labelledby="pp-new-perimeter">
          <SectionLabel id="pp-new-perimeter">2 · Perimeter</SectionLabel>
          <div className="flex flex-wrap gap-1.5">
            <button type="button" className="op-btn op-btn--sm" aria-pressed={pending?.source === "drawn"} onClick={startDraw}>Draw boundary</button>
            <button type="button" className="op-btn op-btn--sm" aria-pressed={picking} onClick={togglePick}>Pick pad</button>
            <label className="op-btn op-btn--sm cursor-pointer">
              Upload KMZ/SHP
              <input type="file" className="hidden" accept=".geojson,.json,.kml,.kmz,.zip" onChange={onFile} aria-label="Upload perimeter" />
            </label>
          </div>
          {pending ? (
            <div className="flex items-center justify-between gap-2 text-[13px]" data-testid="pp-new-perimeter-status">
              <span className="flex items-center gap-1.5 text-fg-primary">
                <Check className="h-3.5 w-3.5 text-status-ok" aria-hidden="true" />
                {SOURCE_WORDS[pending.source]}{area ? ` · ${formatAcres(area)}` : ""}
                {pending.source === "file" ? <span className="op-meta"> · {pending.label}</span> : null}
              </span>
              <button type="button" className="op-meta hover:text-fg-primary" onClick={clearPerimeter}>Clear</button>
            </div>
          ) : <p className="op-meta">No perimeter yet. You can also set it on the plan's page.</p>}
          {pending?.source === "file" && <p className="op-meta">Check the outline on the map before creating the plan.</p>}
          {perimeterError && <p className="text-sm text-accent-red" role="alert">{perimeterError}</p>}
          <div className="flex items-start justify-between gap-2">
            <p className={chosen ? "text-[13px] text-fg-primary" : "op-meta"} data-testid="pp-new-imagery">
              {chosen ? `Base imagery: ${imageryLabel(chosen)}. ${BASE_SAVED_TEXT}`
                : shownLanes?.collects?.length ? BASE_NOT_CHOSEN_TEXT : BASE_PICKED_TEXT}
            </p>
            {chosen && (
              <button type="button" className="op-meta shrink-0 hover:text-fg-primary" aria-label="Clear base imagery"
                onClick={() => setCursorKey(null)}>Clear</button>
            )}
          </div>
        </section>

        {error && <p className="text-sm text-accent-red" role="alert">{error}</p>}
      </SidePanel>

      <div className="relative min-w-0 flex-1">
        <PadPlannerMap ref={mapRef} editable onDraw={onDraw}
          draft={pending && pending.source !== "drawn" ? pending.geometry : null}
          showPads={picking} onPadPick={picking ? onPadPick : null} rasterKey={cursorKey}
          rasterCredit={creditForCapture({ lanes: shownLanes, key: cursorKey })}
          footprint={{ role: "newPlan", include: includeForCapture({ lanes: shownLanes, key: cursorKey }),
            withReference: referenceForCapture({ lanes: shownLanes, key: cursorKey }) }}
          hint={pending ? null : "Draw the boundary, pick a detected pad, or upload a file."}
          readout={[
            { label: "Area", value: area ? formatAcres(area) : null },
            { label: "Perimeter", value: pending ? formatPerimeterFt(pending.geometry) : null },
            { label: "Base imagery", value: chosen ? chipDate(chosen) : null },
          ]}
          showCursor
          bottom={shownLanes ? ((strip) => (
            <ImageryStrip onChip={selectAndZoom(onCursor, strip)} rows={sitePlannerRows({ lanes: shownLanes, cursorKey, markKey: chosenKey,
              markLabel: "base", emptyText: imageryEmptyText(shownLanes.imagery_entitled), ...strip })} />
          )) : null} />
      </div>
    </div>
  );
}

/* ── wells, as tags ─────────────────────────────────────────────────────── */

function WellsField({ wells, onChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [manualLabel, setManualLabel] = useState("");
  const [error, setError] = useState(null);

  async function find(e) {
    e.preventDefault();
    setError(null);
    try { setResults(await lookupWells(q)); } catch (err) { setResults(null); setError(refusalText(err)); }
  }
  const has = new Set(wells.map((w) => w.api10));
  function add(well) {
    if (!has.has(well.api10)) onChange([...wells, well]);
  }
  const digits = q.replace(/[\s-]/g, "");
  const fullNumber = /^\d{10}$|^\d{14}$/.test(digits) && digits.startsWith("42");

  return (
    <div className="flex flex-col gap-1.5">
      <span className="op-label uppercase">Wells</span>
      {wells.length > 0 && (
        <ul className="flex flex-wrap gap-1" aria-label="Wells on the plan">
          {wells.map((w) => (
            <li key={w.api10} className="op-chip h-6 gap-1 rounded-full px-2 py-0 text-[11.5px]" data-testid="pp-new-well">
              <span className="op-mono text-fg-primary">{formatApi10(w.api10)}</span>
              {w.label ? <span className="text-fg-secondary">{w.label}</span> : null}
              {w.source === "manual" ? <span className="text-fg-secondary">planned</span> : null}
              <button type="button" aria-label={`Remove ${formatApi10(w.api10)}`} className="text-fg-secondary hover:text-fg-primary"
                onClick={() => onChange(wells.filter((x) => x.api10 !== w.api10))}>
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <form className="flex gap-1.5" onSubmit={find}>
        <input value={q} onChange={(e) => setQ(e.target.value)} aria-label="API number" placeholder="API number, e.g. 42-383-12345"
          className={`min-w-0 flex-1 ${field}`} />
        <button type="submit" className="op-btn op-btn--sm">Find</button>
      </form>
      {results && (
        <ul className="flex flex-col gap-1" data-testid="pp-well-results">
          {results.wells.length === 0 && <li className="op-meta">No RRC well has that number.</li>}
          {results.wells.map((w) => (
            <li key={w.api10} className="flex items-center justify-between gap-2 text-[12.5px]">
              <span className="min-w-0"><span className="op-mono">{formatApi10(w.api10)}</span>
                <span className="op-meta"> · {[w.operator, w.lease_name && `${w.lease_name}${w.well_no ? ` #${w.well_no}` : ""}`].filter(Boolean).join(" · ")}</span></span>
              {has.has(w.api10)
                ? <span className="op-meta">Added</span>
                : <button type="button" className="op-btn op-btn--sm" onClick={() => add({ api10: w.api10, source: "rrc", label: null })}>Add</button>}
            </li>
          ))}
          {results.truncated && <li className="op-meta">More match. Type more digits.</li>}
        </ul>
      )}
      {results && results.wells.length === 0 && fullNumber && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="pp-manual-well">
          <input value={manualLabel} onChange={(e) => setManualLabel(e.target.value)} maxLength={120} aria-label="Well label"
            placeholder="Label, e.g. Unit 7H" className={field} />
          <button type="button" className="op-btn op-btn--sm"
            onClick={() => add({ api10: digits.slice(0, 10), source: "manual", label: manualLabel.trim() || null })}>
            Add as planned well
          </button>
        </div>
      )}
      {error && <p className="text-sm text-accent-red" role="alert">{error}</p>}
    </div>
  );
}
